/**
 * Telling a self-trade cancellation apart from any other — ROADMAP 5.14.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT ARRIVES AS A CANCELLATION, NOT A REJECTION
 *
 * NSE's PAN-based Self Trade Prevention Check: when an ACTIVE order would
 * match a RESTING order carrying the same PAN, "the active order shall be
 * cancelled by default irrespective of the option set in the active order".
 * The text is `Order cancelled by the System - The order could have resulted
 * in self-trade`.
 *
 * Two things follow, and both are why this file exists.
 *
 * OURS IS ALWAYS THE ORDER THAT DIES. Every order this platform sends is a
 * marketable limit — it crosses the book by construction ([B18]) — so it is
 * always the active side. When a trader's own resting order is on the other
 * side, the exchange cancels ours and leaves theirs.
 *
 * AND A CLASSIFIER WATCHING FOR REJECTIONS MISSES IT ENTIRELY. The order is
 * accepted, then cancelled by the system. Nothing is "rejected". Without
 * this, the leg simply vanishes into the generic CANCELLED bucket alongside
 * a user pressing cancel, and the trader is left asking why one leg of their
 * condor is missing.
 *
 * It becomes reachable the moment a second XTS account is connected under
 * the same PAN, which is exactly what Phase 5 allows.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * Does this cancellation reason mean the exchange stopped a self-trade?
 *
 * Matched on the broker's prose because there is no code for it: XTS passes
 * the exchange's sentence through in `CancelRejectReason`, and the wording
 * differs between exchanges and broker builds. The patterns are deliberately
 * loose — a missed match costs a confusing message, a false match costs a
 * wrong explanation, and neither changes what the system DOES.
 */
export function isSelfTradeCancel(reason) {
    if (!reason)
        return false;
    const s = reason.toLowerCase();
    return (
    // "could have resulted in self-trade", "self trade prevention", "selftrade"
    /self[\s_-]?trade/.test(s) ||
        // The check's own acronym, as some builds report it.
        /\bstpc\b/.test(s));
}
/**
 * What to tell the trader, because the remedy is not the obvious one.
 *
 * A cancelled leg reads like a margin or liquidity problem, and it is
 * neither: nothing is wrong with the order, the account or the market. The
 * trader is standing on both sides of the same contract under one PAN.
 */
export const SELF_TRADE_HELP = 'The exchange cancelled this order to prevent a self-trade: another of your own ' +
    'orders, under the same PAN, was resting on the other side of this contract. ' +
    'Ours is always the order cancelled, because it crosses the book. Check your ' +
    'other accounts for a resting order in this strike before replacing it.';
//# sourceMappingURL=cancelCause.js.map